import api from '~/plugins/apiClient';
import { ApiHandlerWithArgs, ApiHandlerWithData, ApiResponseContext } from '~/plugins/apiClient/_types';
import { FetchListOptions, PaginationMetaSchema } from '../_types';
import {
    DemandDetail,
    DemandDetailSchema,
    DemandDraftDetail,
    DemandDraftDetailSchema,
    DemandFormData,
    FetchDemandsResponse,
    FetchDemandsResponseSchema,
} from './_types';
import { formatApiUrl } from '~/plugins/apiClient/utils';
import { transformZodSchemaFn } from '~/plugins/zod/utils';
import { transformFetchListOptionsToQuery } from '../_transformers';
import { API_DATE_FORMAT } from '~/constants/_constants';
import { omit } from 'lodash-es';
import { DemandState } from '~/enums/_types';

const apiEndpointPrefix = 'demands';

export const fetchDemandsRequest: ApiHandlerWithArgs<
    FetchListOptions,
    ApiResponseContext<FetchDemandsResponse | null>
> = async (opt) => {
    const url = formatApiUrl(apiEndpointPrefix);
    return api.get(url, {
        transform: transformZodSchemaFn(FetchDemandsResponseSchema, url),
        transformMeta: transformZodSchemaFn(PaginationMetaSchema, url),
        params: transformFetchListOptionsToQuery(opt),
    });
};

export const fetchDemandDetailRequest: ApiHandlerWithData<number, ApiResponseContext<DemandDetail>> = async (id) => {
    const url = formatApiUrl(apiEndpointPrefix, id);
    return api.get(url, { transform: transformZodSchemaFn(DemandDetailSchema, url) });
};

export const fetchDemandDetailDraftRequest: ApiHandlerWithData<
    number,
    ApiResponseContext<DemandDraftDetail | null>
> = async (draftId) => {
    const url = formatApiUrl(apiEndpointPrefix, `${draftId}/draft`);
    return api.get(url, {
        transform: transformZodSchemaFn(DemandDraftDetailSchema, url),
    });
};

export const postDemandRequest: ApiHandlerWithData<
    DemandFormData,
    ApiResponseContext<DemandDetail | null, DemandFormData>
> = async (data) => {
    const url = formatApiUrl(apiEndpointPrefix, 'send');
    return api.post(url, transformDemandFormDataToRaw(data), {
        transform: transformZodSchemaFn(DemandDetailSchema, url),
    });
};

export const putDemandRequest: ApiHandlerWithData<
    { id: number; data: DemandFormData },
    ApiResponseContext<DemandDetail | null, DemandFormData>
> = async ({ id, data }) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/send`);
    return api.put(url, transformDemandFormDataToRaw(data), {
        transform: transformZodSchemaFn(DemandDetailSchema, url),
    });
};

export const postDemandDraftRequest: ApiHandlerWithData<
    DemandFormData,
    ApiResponseContext<DemandDraftDetail | null, DemandFormData>
> = async (data) => {
    const url = formatApiUrl(apiEndpointPrefix, 'draft');
    return api.post(url, transformDemandFormDataToRaw(data), {
        transform: transformZodSchemaFn(DemandDraftDetailSchema, url),
    });
};

export const putDemandDraftRequest: ApiHandlerWithData<
    { id: number; data: DemandFormData },
    ApiResponseContext<DemandDraftDetail | null, DemandFormData>
> = async ({ id, data }) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/draft`);
    return api.put(url, transformDemandFormDataToRaw(data), {
        transform: transformZodSchemaFn(DemandDraftDetailSchema, url),
    });
};

export const deleteDemandRequest: ApiHandlerWithData<number, ApiResponseContext<null>> = async (id) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}`);
    return api.delete(url);
};

export const patchDemandChangeStateRequest: ApiHandlerWithData<
    { id: number; newState: DemandState },
    ApiResponseContext<DemandDetail | null>
> = async ({ id, newState }) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/change-state`);
    return api.patch(
        url,
        { Status: newState },
        {
            transform: transformZodSchemaFn(DemandDetailSchema, url),
        }
    );
};

export const patchDemandCreateOrderRequest: ApiHandlerWithData<
    { id: number; changeState: boolean },
    ApiResponseContext<DemandDetail | null>
> = async ({ id, changeState }) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/create-order`);
    return api.patch(url, null, {
        transform: transformZodSchemaFn(DemandDetailSchema, url),
        params: {
            changeState,
        },
    });
};

const transformDemandFormDataToRaw = (formData: DemandFormData) => {
    return {
        ...formData,
        RequiredAt: formData.RequiredAt?.toFormat(API_DATE_FORMAT) ?? null,
        Lines: formData.Lines.map((x) => ({
            ...omit(x, ['Id', 'Item', 'CategoryId', 'Price', 'Nomenclature']),
            ItemId: x.Item.Id,
            RequiredAt: x.RequiredAt == null ? null : x.RequiredAt.toFormat(API_DATE_FORMAT),
        })),
    };
};
